import { FormattedMessage, useIntl } from "react-intl";

// Mui
import { Box, Button, Typography, Skeleton, Chip, Tooltip, useTheme } from "@mui/material";

// Components
import CustomIcon from "components/CustomIcon";
import { RowHead, Row } from "../EntityList/EntityList";
import { SnackBar } from "components/SnackBar";
import { CircledButton } from "pages/ProjectDashboard/styled-components";
import { ButtonWithCounter } from "components/Buttons";

// Hooks
import useBriefCard from "./useBriefCard";

// Others
import icons from "enums/icons";
import { capitalize } from "lodash";
import { urlPaths } from "enums/urlPaths";
import {
    SnackBarEntityTypes,
    TConvertedPeopleList,
    TConvertedCompaniesList,
    getBriefList,
} from "pages/ProjectDashboard/adapters/formattedData";

// Styles
import BriefCardStyles from "./BriefCard.styles";
import { TruncatedText } from "components/TruncatedText";

const selectedEntityInitialState: {
    type: keyof typeof SnackBarEntityTypes | undefined;
    data: (TConvertedPeopleList | TConvertedCompaniesList)[];
} = {
    type: undefined,
    data: [],
};

const BriefCard = ({
    data,
    companyId,
    onClickRelatedButton,
    onClickCreateSearchAi,
}: {
    data: ReturnType<typeof getBriefList>[0];
    companyId: number | undefined;
    onClickRelatedButton: (id: number, type: "insight" | "searchAi" | "brief" | "company" | "people") => void;
    onClickCreateSearchAi?: () => void;
}) => {
    const intl = useIntl();
    const theme = useTheme();
    const classes = BriefCardStyles({ isDisabled: data.isDisabled, isCompleted: data.isCompleted });

    const {
        chips,
        wrapperRef,
        shouldOpenSnackBar,
        popOverAnchorEl,
        selectedEntity,
        hoverChipId,
        clickedChipId,
        setSelectedEntity,
        setClickedChipId,
        handleClickCircledCount,
        handleGotoBrief,
        handleCreateSearchAI,
        handleCloseSnackBar,
    } = useBriefCard({ data, onClickCreateSearchAi });

    const LoadingData = () => {
        return (
            <>
                <Skeleton variant="rectangular" animation="wave" height={28} sx={{ mb: 0.5 }} />
                <Skeleton variant="rectangular" animation="wave" height={28} sx={{ mb: 0.5 }} />
                <Skeleton variant="rectangular" animation="wave" height={28} sx={{ mb: 0.5 }} />
                <Skeleton variant="rectangular" animation="wave" height={28} sx={{ mb: 0.5 }} />
                <Skeleton variant="rectangular" animation="wave" height={28} />
            </>
        );
    };

    return (
        <>
            <Box className={classes.cardWrapper}>
                <Box className={classes.cardInner}>
                    <Box className={classes.mainInfo}>
                        <Box mb={2}>
                            <Typography className={classes.cardTitle}>
                                <TruncatedText>{data.name}</TruncatedText>
                            </Typography>
                            {!data.isCompleted && (
                                <Box sx={{ display: "flex", maxWidth: 120 }}>
                                    <TruncatedText>{data.meta?.relatedInsight?.name}</TruncatedText>
                                </Box>
                            )}
                        </Box>
                        <Box className={classes.actionsWrapper}>
                            {!data.isDisabled && (
                                <Tooltip
                                    arrow
                                    placement="bottom-start"
                                    title={intl.formatMessage({ id: "dashboard.insights.eyeButton.tooltip" })}
                                >
                                    <CircledButton
                                        size={data.isCompleted ? 24 : 28}
                                        color="hovered2"
                                        component="button"
                                        onClick={() => handleGotoBrief(`${urlPaths.BriefReview}/${companyId}`)}
                                    >
                                        <CustomIcon
                                            icon={icons.eye}
                                            sx={{
                                                fontSize: 16,
                                                color: "button.active",
                                                "&:hover": {
                                                    backgroundColor: "button.active",
                                                },
                                            }}
                                        />
                                    </CircledButton>
                                </Tooltip>
                            )}
                            <Tooltip
                                arrow
                                placement="bottom-start"
                                title={intl.formatMessage({ id: "dashboard.insights.linkButton.tooltip" })}
                            >
                                <CircledButton
                                    size={data.isCompleted ? 24 : 28}
                                    color={data.shouldHighlight ? "hovered" : "hovered2"}
                                    component="button"
                                    onClick={() => onClickRelatedButton(data.id, "brief")}
                                >
                                    <CustomIcon
                                        icon={icons.link}
                                        sx={{
                                            backgroundColor: data.shouldHighlight ? "button.active" : "transparent",
                                            fontSize: 16,
                                            color: "button.active",
                                            "&:hover": {
                                                backgroundColor: "button.active",
                                            },
                                        }}
                                    />
                                </CircledButton>
                            </Tooltip>
                        </Box>
                    </Box>
                </Box>

                {/* Options */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    {!data.isCompleted && (
                        <Button
                            sx={{
                                background: data.isDisabled
                                    ? theme.palette.common.white
                                    : theme.palette.others.green100,
                                color: data.isDisabled
                                    ? theme.palette.button.active.from
                                    : theme.palette.others.green900,
                                minWidth: 140,
                                fontSize: 13,
                                height: 32,
                                padding: "6px 16px",
                                border: data.isDisabled ? `1px solid ${theme.palette.primary.dark}` : `initial`,
                                "& .MuiButton-startIcon > *:nth-of-type(1)": {
                                    fontSize: "16px !important",
                                    color: theme.palette.others.green900,
                                    transition: "all 0.3s ease",
                                    ml: 0,
                                },
                                "&:hover": {
                                    background: data.isDisabled
                                        ? theme.palette.common.white
                                        : theme.palette.others.green900,
                                    color: data.isDisabled
                                        ? theme.palette.secondary.dark
                                        : theme.palette.others.green100,
                                    fontWeight: data.isDisabled ? "bold" : "normal",
                                    filter: data.isDisabled
                                        ? "drop-shadow(0px 1px 0px rgba(0, 0, 0, 0.03)) drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.05)) drop-shadow(0px 10px 10px rgba(0, 0, 0, 0.1))"
                                        : "none",

                                    "& .MuiButton-startIcon > *:nth-of-type(1)": {
                                        color: data.isDisabled
                                            ? theme.palette.secondary.dark
                                            : theme.palette.others.green100,
                                    },
                                },
                            }}
                            startIcon={<CustomIcon icon={data.isDisabled ? icons.pencil : icons.flashLight} />}
                            onClick={
                                data.isDisabled
                                    ? () => handleGotoBrief(`${urlPaths.Brief}/${companyId}`)
                                    : handleCreateSearchAI
                            }
                        >
                            {data.isDisabled ? "Finish Brief" : "Start SearchAI"}
                        </Button>
                    )}

                    {!data.isCompleted && (
                        <Box className={classes.chipsWrapper}>
                            {chips.map((chip) => {
                                const chipType =
                                    SnackBarEntityTypes[
                                        `${chip.label.toLowerCase() as keyof typeof SnackBarEntityTypes}`
                                    ];

                                return (
                                    <Chip
                                        key={chip.id}
                                        className={classes.chip}
                                        variant="filled"
                                        label={chip.label}
                                        size="small"
                                        onClick={(ev) => handleClickCircledCount(ev, chipType, chip)}
                                        onDelete={(ev) => handleClickCircledCount(ev, chipType, chip)}
                                        disabled={!chip.count}
                                        deleteIcon={
                                            <ButtonWithCounter
                                                count={chip.count}
                                                isDisabled={!chip.count}
                                                shouldShowIcon={hoverChipId === chip.id || clickedChipId === chip.id}
                                                size={20}
                                                callback={(ev) => handleClickCircledCount(ev, chipType, chip)}
                                                colors={["hovered", "info"]}
                                            />
                                        }
                                    />
                                );
                            })}
                        </Box>
                    )}
                </Box>
            </Box>

            <SnackBar
                maxWidth={wrapperRef.current?.getBoundingClientRect().width || 400}
                shouldOpen={shouldOpenSnackBar}
                anchorElement={popOverAnchorEl}
                title={<strong>{capitalize(selectedEntity.type)}</strong>}
                handleInfoClose={(ev) => {
                    handleCloseSnackBar(ev);
                    setSelectedEntity(selectedEntityInitialState);
                    setClickedChipId(null);
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                }}
                {...(wrapperRef.current
                    ? {
                          anchorPosition: {
                              top: wrapperRef.current?.getBoundingClientRect().bottom + 5,
                              left:
                                  wrapperRef.current?.getBoundingClientRect().left +
                                  wrapperRef.current?.getBoundingClientRect().width / 2,
                          },
                      }
                    : {})}
                content={
                    <>
                        <RowHead
                            title={
                                <FormattedMessage
                                    id={`project.dashboard.popup.header.${
                                        selectedEntity.type === SnackBarEntityTypes.company ? "company" : "people"
                                    }.subterm`}
                                />
                            }
                        />
                        <Box sx={{ px: 2, maxHeight: 300, overflowY: "scroll" }}>
                            {selectedEntity.type && !Boolean(selectedEntity.data.length) && <LoadingData />}

                            {selectedEntity.type === SnackBarEntityTypes.company &&
                                (selectedEntity.data as TConvertedCompaniesList[])?.map((c) => (
                                    <Row key={c.id} term={c.name ?? ""} value={c.currentCapitalStructure ?? ""} />
                                ))}

                            {(selectedEntity.type === SnackBarEntityTypes.people ||
                                selectedEntity.type === SnackBarEntityTypes.shortlist) &&
                                (selectedEntity.data as TConvertedPeopleList[])?.map((p) => (
                                    <Row key={p.id} term={p.name} subTerm={p.jobTitle} value={p.company ?? ""} />
                                ))}
                        </Box>
                    </>
                }
            />
        </>
    );
};

export default BriefCard;
